'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { optimizeImageForBackground, shouldOptimizeImage } from '@/lib/imageOptimization';

const CUSTOM_BACKGROUND_STORAGE_KEY = 'evolutto-custom-background-image';
const CUSTOM_BACKGROUND_EVENT = 'custom-background-changed';
const MAX_BASE64_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png'];
export const CUSTOM_BACKGROUND_PREFIX = 'custom:';

export interface CustomBackground {
  id: string;
  dataUrl: string;
}

const computeBackgroundId = (dataUrl: string): string => {
  let hash = 0;
  for (let i = 0; i < dataUrl.length; i += 1) {
    hash = (hash << 5) - hash + dataUrl.charCodeAt(i);
    hash |= 0;
  }
  const base = Math.abs(hash).toString(36);
  return `bg_${base}_${dataUrl.length}`;
};

const normalizeBackground = (value: CustomBackground): CustomBackground => {
  const id = value.id || computeBackgroundId(value.dataUrl);
  return { id, dataUrl: value.dataUrl };
};

const parseStoredBackgrounds = (value: string | null): CustomBackground[] => {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => {
          if (item && typeof item === 'object' && typeof item.dataUrl === 'string') {
            return normalizeBackground({ id: (item as any).id, dataUrl: item.dataUrl });
          }
          if (typeof item === 'string' && item.startsWith('data:')) {
            return normalizeBackground({ id: computeBackgroundId(item), dataUrl: item });
          }
          return null;
        })
        .filter((item): item is CustomBackground => item !== null);
    }
  } catch {
    // ignore
  }

  if (value.startsWith('data:')) {
    return [normalizeBackground({ id: computeBackgroundId(value), dataUrl: value })];
  }

  return [];
};

export function useCustomBackgroundImage() {
  const [customBackgrounds, setCustomBackgrounds] = useState<CustomBackground[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedInitialValue = useRef(false);
  const backgroundsRef = useRef<CustomBackground[]>([]);

  const persistBackgrounds = useCallback((images: CustomBackground[]) => {
    backgroundsRef.current = images;
    setCustomBackgrounds(images);

    if (typeof window === 'undefined') {
      return;
    }

    try {
      if (images.length === 0) {
        localStorage.removeItem(CUSTOM_BACKGROUND_STORAGE_KEY);
      } else {
        localStorage.setItem(CUSTOM_BACKGROUND_STORAGE_KEY, JSON.stringify(images));
      }
      window.dispatchEvent(
        new CustomEvent<CustomBackground[]>(CUSTOM_BACKGROUND_EVENT, { detail: images }),
      );
    } catch (storageError) {
      console.error('Erro ao persistir fundos customizados:', storageError);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const storedValue = localStorage.getItem(CUSTOM_BACKGROUND_STORAGE_KEY);
      const parsed = parseStoredBackgrounds(storedValue);
      backgroundsRef.current = parsed;
      setCustomBackgrounds(parsed);
    } catch (storageError) {
      console.error('Erro ao carregar fundos customizados:', storageError);
    } finally {
      hasLoadedInitialValue.current = true;
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleCustomBackgroundChange = (event: Event) => {
      const customEvent = event as CustomEvent<CustomBackground[] | null>;
      if (Array.isArray(customEvent.detail)) {
        backgroundsRef.current = customEvent.detail;
        setCustomBackgrounds(customEvent.detail);
      } else if (!customEvent.detail) {
        backgroundsRef.current = [];
        setCustomBackgrounds([]);
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== CUSTOM_BACKGROUND_STORAGE_KEY) {
        return;
      }
      const parsed = parseStoredBackgrounds(event.newValue);
      backgroundsRef.current = parsed;
      setCustomBackgrounds(parsed);
    };

    window.addEventListener(CUSTOM_BACKGROUND_EVENT, handleCustomBackgroundChange as EventListener);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(
        CUSTOM_BACKGROUND_EVENT,
        handleCustomBackgroundChange as EventListener,
      );
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const removeCustomBackground = useCallback(
    (id: string) => {
      const filtered = backgroundsRef.current.filter((item) => item.id !== id);
      persistBackgrounds(filtered);
    },
    [persistBackgrounds],
  );

  const uploadCustomBackground = useCallback(
    async (
      file: File,
    ): Promise<{ success: boolean; message?: string; dataUrl?: string; id?: string }> => {
      if (typeof window === 'undefined') {
        return { success: false, message: 'Ambiente indisponível para upload.' };
      }

      setError(null);

      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        const message = 'Formato inválido. Utilize uma imagem .jpg ou .png.';
        setError(message);
        return { success: false, message };
      }

      const estimatedBase64Size = Math.ceil(file.size / 3) * 4;
      if (estimatedBase64Size > MAX_BASE64_SIZE_BYTES) {
        const message =
          'A imagem é muito grande. Utilize um arquivo com até 5MB após a conversão para Base64.';
        setError(message);
        return { success: false, message };
      }

      setIsUploading(true);

      try {
        // Ler arquivo como data URL
        const base64Image = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            if (typeof reader.result === 'string') {
              resolve(reader.result);
            } else {
              reject(new Error('Não foi possível ler o arquivo selecionado.'));
            }
          };
          reader.onerror = () => reject(new Error('Falha ao ler o arquivo selecionado.'));
          reader.readAsDataURL(file);
        });

        // Verificar tamanho antes de otimizar
        const [, base64Payload = ''] = base64Image.split(',');
        const payloadBytes = Math.ceil((base64Payload.length * 3) / 4);

        if (payloadBytes > MAX_BASE64_SIZE_BYTES) {
          const message = 'A imagem convertida ultrapassou 5MB. Escolha um arquivo menor.';
          setError(message);
          return { success: false, message };
        }

        // Otimizar imagem se necessário
        let finalImage = base64Image;
        if (shouldOptimizeImage(base64Image)) {
          console.log('[useCustomBackgroundImage] Otimizando imagem durante upload...');
          const optimizationResult = await optimizeImageForBackground(base64Image);
          
          if (optimizationResult.success && optimizationResult.dataUrl) {
            finalImage = optimizationResult.dataUrl;
            console.log('[useCustomBackgroundImage] Imagem otimizada:', {
              originalSize: `${((optimizationResult.originalSize || 0) / 1024).toFixed(2)} KB`,
              optimizedSize: `${((optimizationResult.optimizedSize || 0) / 1024).toFixed(2)} KB`,
            });
          } else {
            console.warn('[useCustomBackgroundImage] Falha na otimização, usando original:', optimizationResult.error);
            // Continuar com imagem original
          }
        }

        // Verificar se já existe (comparar com imagem otimizada)
        const existing = backgroundsRef.current.find((item) => item.dataUrl === finalImage);
        if (existing) {
          return { success: true, dataUrl: existing.dataUrl, id: existing.id };
        }

        let id = computeBackgroundId(finalImage);
        if (backgroundsRef.current.some((item) => item.id === id)) {
          id = `${id}_${Date.now().toString(36)}`;
        }

        const updatedImages = [...backgroundsRef.current, { id, dataUrl: finalImage }];
        persistBackgrounds(updatedImages);

        return { success: true, dataUrl: finalImage, id };
      } catch (uploadError) {
        console.error('Erro ao processar upload do fundo:', uploadError);
        const message =
          uploadError instanceof Error
            ? uploadError.message
            : 'Erro desconhecido ao processar a imagem.';
        setError(message);
        return { success: false, message };
      } finally {
        setIsUploading(false);
      }
    },
    [persistBackgrounds],
  );

  const resetError = useCallback(() => setError(null), []);

  const resolveCustomBackgroundPath = useCallback((path?: string | null) => {
    if (!path) return undefined;
    if (path.startsWith(CUSTOM_BACKGROUND_PREFIX)) {
      const id = path.slice(CUSTOM_BACKGROUND_PREFIX.length);
      return backgroundsRef.current.find((item) => item.id === id)?.dataUrl;
    }
    return path;
  }, []);

  const getCustomBackgroundIdByDataUrl = useCallback((dataUrl?: string | null) => {
    if (!dataUrl) return undefined;
    const match = backgroundsRef.current.find((item) => item.dataUrl === dataUrl);
    return match?.id;
  }, []);

  const getCustomBackgroundById = useCallback((id?: string | null) => {
    if (!id) return undefined;
    return backgroundsRef.current.find((item) => item.id === id);
  }, []);

  return {
    customBackgrounds,
    isUploading,
    error,
    hasLoadedInitialValue: hasLoadedInitialValue.current,
    uploadCustomBackground,
    removeCustomBackground,
    resetError,
    resolveCustomBackgroundPath,
    getCustomBackgroundIdByDataUrl,
    getCustomBackgroundById,
  };
}
